// 2026-06-25: Phase 12 v3 — Work Center Capabilities with horizontal tabs
// Top tabs: shops. For shops with multiple machine types, secondary tabs.
// Matrix takes full page width.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { listWorkCenters } from '../../Services/app/workorderservice';
import { customerbasedshift, cleanCustomerId } from '../../Services/app/operatorservice';
import { customerbaseddevices } from '../../Services/app/companyservice';
import { shiftadd } from '../../Services/app/masterservice';
import Swal from 'sweetalert2';
import axios from 'axios';
import { MachineIcon, iconForMachine } from '../../Shared/machineIcons';
import './MachineCapabilities.css';

const BASE = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');

function classifyMachine(name) {
  if (!name) return 'OTHER';
  const u = String(name).toUpperCase();
  if (u.includes('TMC')) return 'TMC';
  if (u.includes('VMC')) return 'VMC';
  if (u.includes('HMC')) return 'HMC';
  if (u.includes('VTL')) return 'VTL';
  if (u.includes('TC'))  return 'TC';
  if (u.includes('MC'))  return 'MC';
  return 'OTHER';
}

const MACHINE_TYPE_META = {
  VMC: { label: 'Vertical Machining Center', color: '#2563eb' },
  HMC: { label: 'Horizontal Machining Center', color: '#1d4ed8' },
  VTL: { label: 'Vertical Turret Lathe', color: '#0891b2' },
  TC:  { label: 'Turning Center (Lathe)', color: '#0e7490' },
  TMC: { label: 'Turn-Mill Center', color: '#7c3aed' },
  MC:  { label: 'Machining Center', color: '#4f46e5' },
  OTHER:{ label: 'Other', color: '#6b7280' },
};
const MACHINE_TYPE_ORDER = ['VMC', 'HMC', 'MC', 'TMC', 'TC', 'VTL', 'OTHER'];


// Client-side image resize + JPEG re-encode for storage.
function resizeImageToDataUri(file, maxDim = 480, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

const PROFILE_BY_TYPE = {
  TC:  ['TURNING', 'DRILLING', 'TAPPING', 'REAMING'],
  TMC: ['TURNING', 'MILLING', 'DRILLING', 'TAPPING', 'BORING', 'REAMING'],
  MC:  ['MILLING', 'DRILLING', 'TAPPING', 'BORING', 'REAMING'],
  VMC: ['MILLING', 'DRILLING', 'TAPPING', 'BORING'],
  HMC: ['MILLING', 'DRILLING', 'TAPPING', 'BORING', 'REAMING'],
  VTL: ['TURNING', 'BORING', 'REAMING'],
};

export default function MachineCapabilities() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [shops, setShops] = useState([]);
  const [operations, setOperations] = useState([]);
  const [capabilities, setCapabilities] = useState({});
  const [machineGroups, setMachineGroups] = useState([]);
  const [machineSpecsMap, setMachineSpecsMap] = useState({});
  const [viewPhoto, setViewPhoto] = useState(null); // {url, name} for lightbox
  const [nameToUuid, setNameToUuid] = useState({});
  const [wcs, setWcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopTab, setShopTab] = useState(null);
  const [typeTab, setTypeTab] = useState('ALL');
  const [dirty, setDirty] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [procs, ops, caps, mgs, wcList, photos] = await Promise.all([
        customerbasedshift(customerId, 'process_master').catch(() => []),
        customerbasedshift(customerId, 'operation_master').catch(() => []),
        customerbasedshift(customerId, 'machine_capabilities').catch(() => []),
        customerbasedshift(customerId, 'machinegroups').catch(() => []),
        listWorkCenters(customerId).then((r)=>r).catch(() => []),
        Promise.resolve(null).catch(() => []),
      ]);
      const procList = Array.isArray(procs?.[0]?.value) ? procs[0].value : [];
      const visibleShops = procList.filter((p) => p.active !== false)
        .sort((a, b) => (a.workflow_order || 99) - (b.workflow_order || 99));
      setShops(visibleShops);
      setOperations(Array.isArray(ops?.[0]?.value) ? ops[0].value : []);
      setCapabilities(caps?.[0]?.value && typeof caps[0].value === 'object' ? caps[0].value : {});
      setMachineGroups(Array.isArray(mgs?.[0]?.value) ? mgs[0].value : []);
      // machineSpecsMap populated by separate effect (one call per device_id)
      setWcs(wcList || []);
      if (visibleShops.length > 0 && !shopTab) setShopTab(visibleShops[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId, shopTab]);

  // Phase 12 v6: fetch TB devices once to build machine NAME -> UUID map (needed for /machine-specs)
  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        const r = await customerbaseddevices(customerId, 1000, 0);
        const arr = r?.data?.data || r?.data || [];
        const map = {};
        arr.forEach((d) => {
          const did = d.id?.id || d.id;
          if (d.name && did) map[d.name] = did;
        });
        setNameToUuid(map);
      } catch (e) { console.error('[MC] device fetch failed', e); }
    })();
  }, [customerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);


  // All machines for selected shop
  const machinesForShop = useMemo(() => {
    if (!shopTab) return [];
    const seen = new Map();
    machineGroups.filter((g) => g.shop_type === shopTab.code).forEach((g) => {
      (g.machines || []).forEach((m) => {
        const id = typeof m === 'string' ? m : m?.id;
        const name = typeof m === 'string' ? m : (m?.name || m?.id);
        if (id && !seen.has(id)) seen.set(id, { id, name });
      });
    });
    wcs.filter((w) => w.shop_type === shopTab.code).forEach((w) => {
      (Array.isArray(w.machine_ids) ? w.machine_ids : []).forEach((id) => {
        if (id && !seen.has(id)) seen.set(id, { id, name: id });
      });
    });
    return Array.from(seen.values());
  }, [shopTab, machineGroups, wcs]);

  const opsForShop = useMemo(() => {
    if (!shopTab) return [];
    let list = operations.filter((o) => o.shop_type === shopTab.code && o.active !== false);
    // Phase 13 2026-06-26: narrow op columns by machine type present.
    // - Specific type selected: only show ops that type supports.
    // - ALL selected: only show ops supported by at least one type present in this shop.
    if (typeTab !== "ALL" && PROFILE_BY_TYPE[typeTab]) {
      const allowed = new Set(PROFILE_BY_TYPE[typeTab]);
      list = list.filter((o) => allowed.has(o.code));
    } else if (typeTab === "ALL" && machinesForShop.length > 0) {
      // Derive types present locally to avoid temporal dead zone w/ typesPresent below
      const localTypes = new Set();
      machinesForShop.forEach((m) => localTypes.add(classifyMachine(m.name)));
      const union = new Set();
      let hasProfileTypes = false;
      localTypes.forEach((t) => {
        if (PROFILE_BY_TYPE[t]) {
          hasProfileTypes = true;
          PROFILE_BY_TYPE[t].forEach((op) => union.add(op));
        }
      });
      if (hasProfileTypes) list = list.filter((o) => union.has(o.code));
    }
    return list.sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
  }, [shopTab, operations, typeTab, machinesForShop]);

  const machinesByType = useMemo(() => {
    const groups = {};
    machinesForShop.forEach((m) => {
      const t = classifyMachine(m.name);
      if (!groups[t]) groups[t] = [];
      groups[t].push(m);
    });
    Object.values(groups).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }, [machinesForShop]);


  // Phase 12 v5: fetch machine-specs (incl. image_url) for visible machines
  useEffect(() => {
    if (!machinesForShop || machinesForShop.length === 0) return;
    const toFetch = machinesForShop.filter((m) => !machineSpecsMap[m.id]);
    if (toFetch.length === 0) return;
    (async () => {
      const additions = {};
      await Promise.all(toFetch.map(async (m) => {
        try {
          const did = nameToUuid[m.name] || m.id;
          const r = await axios.get(BASE + "/api/operator-assignment/machine-specs?device_id=" + did);
          additions[m.id] = r.data || {};
        } catch (e) { additions[m.id] = {}; }
      }));
      setMachineSpecsMap((prev) => ({ ...prev, ...additions }));
    })();
  }, [machinesForShop, nameToUuid]);  // eslint-disable-line react-hooks/exhaustive-deps

  const typesPresent = useMemo(() => {
    const present = Object.keys(machinesByType);
    return MACHINE_TYPE_ORDER.filter((t) => present.includes(t));
  }, [machinesByType]);

  // Reset typeTab when shop changes
  useEffect(() => { setTypeTab('ALL'); }, [shopTab?.code]);

  // Filter visible rows based on typeTab
  const visibleMachines = useMemo(() => {
    if (typeTab === 'ALL') return machinesForShop;
    return machinesByType[typeTab] || [];
  }, [typeTab, machinesForShop, machinesByType]);

  const machineCountByShop = useMemo(() => {
    const m = {};
    shops.forEach((s) => {
      const ids = new Set();
      machineGroups.filter((g) => g.shop_type === s.code).forEach((g) => {
        (g.machines || []).forEach((mm) => ids.add(typeof mm === 'string' ? mm : mm?.id));
      });
      wcs.filter((w) => w.shop_type === s.code).forEach((w) => {
        (Array.isArray(w.machine_ids) ? w.machine_ids : []).forEach((id) => ids.add(id));
      });
      m[s.code] = ids.size;
    });
    return m;
  }, [shops, machineGroups, wcs]);


  // Phase 12 v5: photo upload — POST to same /machine-image endpoint as Machines page
  const handlePhotoSelect = async (deviceId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire('Invalid file', 'Please pick an image (jpg/png/webp).', 'warning'); return;
    }
    if (file.size > 500 * 1024) {
      Swal.fire('Too large', 'Max 500 KB. Please compress the image.', 'warning'); return;
    }
    try {
      const fd = new FormData();
      fd.append('customer_id', customerId);
      const uploadDid = nameToUuid[(machinesForShop.find(m=>m.id===deviceId)||{}).name] || deviceId;
      fd.append('device_id', String(uploadDid));
      fd.append('image', file);
      const res = await fetch(BASE + "/api/operator-assignment/machine-image", { method: 'POST', body: fd });
      const j = await res.json();
      if (j.url) {
        setMachineSpecsMap((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] || {}), image_url: j.url } }));
        Swal.fire({ icon: 'success', title: 'Photo saved', timer: 900, showConfirmButton: false });
      } else {
        Swal.fire('Upload failed', j.message || 'Try again', 'error');
      }
    } catch (e) {
      Swal.fire('Upload error', e.message, 'error');
    }
  };
  const handlePhotoRemove = async (deviceId) => {
    // No remove endpoint in machine-specs API; clear the URL locally
    setMachineSpecsMap((prev) => ({ ...prev, [deviceId]: { ...(prev[deviceId] || {}), image_url: '' } }));
  };

  const toggleCap = (id, code) => {
    setCapabilities((prev) => {
      const cur = new Set(prev[id] || []);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      return { ...prev, [id]: Array.from(cur) };
    });
    setDirty(true);
  };
  const selectAllFor = (id, codes) => { setCapabilities((p) => ({ ...p, [id]: codes })); setDirty(true); };
  const clearAllFor = (id)        => { setCapabilities((p) => ({ ...p, [id]: [] }));     setDirty(true); };

  const applyTypeProfile = (type) => {
    const PROFILE = PROFILE_BY_TYPE[type] || [];
    const shopCodes = new Set(opsForShop.map((o) => o.code));
    const intersect = PROFILE.filter((c) => shopCodes.has(c));
    if (intersect.length === 0) {
      Swal.fire('No matching ops', `Shop "${shopTab.name}" has no ops matching the ${type} profile.`, 'info');
      return;
    }
    const next = { ...capabilities };
    (machinesByType[type] || []).forEach((m) => { next[m.id] = intersect; });
    setCapabilities(next);
    setDirty(true);
    Swal.fire({ icon: 'success', title: `Applied ${type} profile to ${machinesByType[type].length} machines`, timer: 1100, showConfirmButton: false });
  };

  const save = async () => {
    try {
      await shiftadd({ machine_capabilities: capabilities }, customerId, 'SERVER_SCOPE');
      Swal.fire({ icon: 'success', title: 'Saved', timer: 900, showConfirmButton: false });
      setDirty(false);
    } catch (e) { Swal.fire('Error', e?.response?.data?.message || e.message, 'error'); }
  };

  const totals = useMemo(() => {
    let mt = 0, cc = 0;
    Object.values(capabilities).forEach((v) => { if (Array.isArray(v) && v.length) { mt++; cc += v.length; } });
    return { mt, cc };
  }, [capabilities]);

  return (
    <div className="mc-page-v3 pages" style={{ paddingTop: 24 }}>
      <div className="mc-header">
        <div>
          <h5 style={{ margin: 0 }}>Work Center Capabilities</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Pick a shop · pick a machine type · tick the operations each machine can perform
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            <strong>{totals.mt}</strong> machines
          </div>
          <button className="mc-btn-secondary" onClick={fetchAll}>Refresh</button>
          <button className="mc-btn-primary" onClick={save} disabled={!dirty}>{dirty ? '● Save changes' : 'Saved'}</button>
        </div>
      </div>

      {/* SHOP TABS (top) */}
      <div className="mc-shop-tabs">
        {loading ? <span style={{ color: '#9ca3af' }}>Loading…</span> :
          shops.map((s) => (
            <button key={s.code}
                    className={`mc-shop-tab ${shopTab?.code === s.code ? 'mc-shop-tab-active' : ''}`}
                    onClick={() => setShopTab(s)}
                    style={shopTab?.code === s.code ? { borderColor: s.color, color: s.color } : {}}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, display: 'inline-block', marginRight: 6 }} />
              {s.name}
              <span className="mc-shop-tab-count">{machineCountByShop[s.code] || 0}</span>
            </button>
          ))
        }
      </div>

      {/* TYPE SUB-TABS (only when shop has multiple types) */}
      {shopTab && typesPresent.length > 1 && (
        <div className="mc-type-tabs">
          <button className={`mc-type-tab ${typeTab === 'ALL' ? 'mc-type-tab-active' : ''}`}
                  onClick={() => setTypeTab('ALL')}>
            All <span className="mc-type-tab-count">{machinesForShop.length}</span>
          </button>
          {typesPresent.map((t) => {
            const meta = MACHINE_TYPE_META[t];
            const count = (machinesByType[t] || []).length;
            return (
              <button key={t}
                      className={`mc-type-tab ${typeTab === t ? 'mc-type-tab-active' : ''}`}
                      onClick={() => setTypeTab(t)}
                      style={typeTab === t ? { background: meta.color, color: '#fff', borderColor: meta.color } : {}}>
                <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 6 }}>
                  <MachineIcon type={t} size={20} color={typeTab === t ? '#fff' : meta.color} />
                </span>
                {t} <span style={{ opacity: 0.85, marginLeft: 4 }}>· {meta.label}</span>
                <span className="mc-type-tab-count" style={typeTab === t ? { background: 'rgba(255,255,255,0.25)', color: '#fff' } : {}}>{count}</span>
              </button>
            );
          })}
          {typeTab !== 'ALL' && PROFILE_BY_TYPE[typeTab] && (
            <button className="mc-bulk-apply" onClick={() => applyTypeProfile(typeTab)}>
              ⚡ Apply {typeTab} profile to all
            </button>
          )}
        </div>
      )}

      {/* MATRIX — full width */}
      <div className="mc-matrix-fullwidth">
        {!shopTab ? <div style={{ padding: 30, color: '#9ca3af' }}>Pick a shop tab above</div> :
          visibleMachines.length === 0 ? (
            <div style={{ padding: 30, color: '#9ca3af', textAlign: 'center' }}>
              <p>No machines in this view.</p>
              <p style={{ fontSize: 12 }}>Add machines via <strong>Settings → Machine Groups</strong>.</p>
            </div>
          ) : opsForShop.length === 0 ? (
            <div style={{ padding: 30, color: '#9ca3af', textAlign: 'center' }}>
              <p>No operations defined for {shopTab.name}.</p>
              <p style={{ fontSize: 12 }}>Add operations via <strong>Settings → Operation Master</strong>.</p>
            </div>
          ) : (
            <table className="mc-table mc-table-wide">
              <thead>
                <tr>
                  <th className="mc-th-machine">Machine ({visibleMachines.length})</th>
                  {opsForShop.map((o) => (
                    <th key={o.code} className="mc-th-op-wide" title={o.name}>
                      <div style={{ background: o.color, padding: '3px 8px', borderRadius: 3, color: '#fff', fontSize: 10, marginBottom: 3, display: 'inline-block' }}>{o.code}</div>
                      <div>{o.name}</div>
                    </th>
                  ))}
                  <th className="mc-th-actions">Bulk</th>
                </tr>
              </thead>
              <tbody>
                {visibleMachines.map((m) => {
                  const t = classifyMachine(m.name);
                  const meta = MACHINE_TYPE_META[t];
                  const caps = new Set(capabilities[m.id] || []);
                  return (
                    <tr key={m.id}>
                      <td className="mc-machine-cell">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="mc-machine-icon-wrap" style={{ borderColor: meta.color, position: 'relative', cursor: 'pointer' }}
                               onClick={() => {
                                 if (machineSpecsMap[m.id]?.image_url) {
                                   setViewPhoto({ url: BASE + machineSpecsMap[m.id].image_url, name: m.name });
                                 } else {
                                   document.getElementById(`photo-up-${m.id}`)?.click();
                                 }
                               }}
                               title={machineSpecsMap[m.id]?.image_url ? 'Click to view full photo' : 'Click to upload photo'}>
                            {machineSpecsMap[m.id]?.image_url ? (
                              <>
                                <img src={BASE + machineSpecsMap[m.id].image_url} alt={m.name} className="mc-machine-photo" />
                                <button className="mc-photo-edit"
                                        onClick={(e) => { e.stopPropagation(); document.getElementById(`photo-up-${m.id}`)?.click(); }}
                                        title="Replace photo">✎</button>
                              </>
                            ) : (
                              <MachineIcon type={iconForMachine({ name: m.name, shopType: shopTab.code })} size={44} color={meta.color} />
                            )}
                            <input id={`photo-up-${m.id}`} type="file" accept="image/*" style={{ display: 'none' }}
                                   onChange={(e) => handlePhotoSelect(m.id, e.target.files?.[0])} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {typeTab === 'ALL' && <span className="mc-type-mini-pill" style={{ background: meta.color }}>{t}</span>}
                              <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{m.name}</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              <span style={{ color: meta.color, fontWeight: 600 }}>{MACHINE_TYPE_META[t]?.label || t}</span>
                              <span style={{ color: '#9ca3af', marginLeft: 6 }}>· {caps.size}/{opsForShop.length} ops</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      {opsForShop.map((o) => {
                        const on = caps.has(o.code);
                        return (
                          <td key={o.code} className="mc-cap-cell">
                            <button className={`mc-cap-btn ${on ? 'mc-cap-on' : ''}`}
                                    onClick={() => toggleCap(m.id, o.code)}
                                    style={on ? { background: o.color, borderColor: o.color } : {}}>
                              {on ? '✓' : ''}
                            </button>
                          </td>
                        );
                      })}
                      <td className="mc-bulk-cell">
                        <button className="mc-bulk-btn" onClick={() => selectAllFor(m.id, opsForShop.map(o => o.code))}>All</button>
                        <button className="mc-bulk-btn" onClick={() => clearAllFor(m.id)}>None</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {/* Phase 12 v8: Photo lightbox */}
      {viewPhoto && (
        <div className="mc-lightbox" onClick={() => setViewPhoto(null)}>
          <div className="mc-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <div className="mc-lightbox-head">
              <div style={{ fontWeight: 700, fontSize: 15 }}>{viewPhoto.name}</div>
              <button className="mc-lightbox-close" onClick={() => setViewPhoto(null)}>×</button>
            </div>
            <img src={viewPhoto.url} alt={viewPhoto.name} className="mc-lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
}
